import {
  EDUCATION_LEVEL_OPTIONS,
  type EDUCATION_LEVEL_OPTIONS_TYPE,
  LEARNING_GOAL_OPTIONS,
  type LEARNING_GOAL_OPTIONS_TYPE,
} from 'bundles/onboarding-common/constants';
import type { SelectedRole, Skill } from 'bundles/onboarding-common/types';

const formatEducationLevel = (educationLevel?: EDUCATION_LEVEL_OPTIONS_TYPE) => {
  if (!educationLevel) {
    return undefined;
  }
  switch (educationLevel) {
    case EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_LESS_THAN_HIGH_SCHOOL_DIPLOMA:
      return 1;
    case EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_HIGH_SCHOOL_DIPLOMA:
      return 4;
    case EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_COLLEGE_NO_DEGREE:
      return 5;
    case EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_ASSOCIATE_DEGREE:
      return 6;
    case EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_BACHELOR_DEGREE:
      return 8;
    case EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_MASTERS_DEGREE:
      return 9;
    case EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_PROFESSIONAL_DEGREE:
      return 10;
    case EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_DOCTORATE_DEGREE:
      return 11;
    default:
      return 0;
  }
};

const formatLearningGoal = (learningGoal?: LEARNING_GOAL_OPTIONS_TYPE) => {
  if (!learningGoal) {
    return undefined;
  }
  switch (learningGoal) {
    case LEARNING_GOAL_OPTIONS.LEARNING_GOAL_IMPROVE_SKILLS:
      return 1;
    case LEARNING_GOAL_OPTIONS.LEARNING_GOAL_CHANGE_CAREER:
      return 2;
    case LEARNING_GOAL_OPTIONS.LEARNING_GOAL_LAND_NEW_JOB:
      return 3;
    case LEARNING_GOAL_OPTIONS.LEARNING_GOAL_PERSONAL_INTEREST:
      return 4;
    default:
      return 0;
  }
};

const formatOccupation = (occupation?: Record<string, string>) => {
  if (!occupation) {
    return undefined;
  }

  if (occupation?.id) {
    return {
      typeName: 'OccupationId',
      definition: {
        id: occupation.id,
      },
    };
  } else {
    return {
      typeName: 'UserGeneratedOccupation',
      definition: {
        name: occupation?.otherText,
      },
    };
  }
};

const formatSkill = (skill: Skill) => {
  return {
    typeName: 'UserInterestSkillId',
    definition: {
      skillId: skill.id,
    },
  };
};

const formatOccupationPreferences = (occupationPreferences?: Array<SelectedRole>) => {
  return occupationPreferences?.map((occupation) => {
    if (occupation.type === 'CUSTOM') {
      return {
        occupation: {
          typeName: 'UserGeneratedOccupation',
          definition: {
            name: occupation.name,
          },
        },
      };
    } else {
      return {
        occupation: {
          typeName: 'OccupationId',
          definition: {
            id: occupation.id,
          },
        },
      };
    }
  });
};

export const formatDemographics = ({
  learningGoal,
  educationLevel,
  currentOccupation,
  skillsWanted,
  occupationPreferences,
}: {
  learningGoal?: LEARNING_GOAL_OPTIONS_TYPE;
  educationLevel?: EDUCATION_LEVEL_OPTIONS_TYPE;
  currentOccupation?: Record<string, string>;
  skillsWanted?: Array<Skill>;
  occupationPreferences?: Array<SelectedRole>;
}) => {
  return {
    educationalAttainment: formatEducationLevel(educationLevel),
    currentOccupation: formatOccupation(currentOccupation),
    learningGoal: formatLearningGoal(learningGoal),
    skillsWantedV2: skillsWanted?.map(formatSkill),
    interestedCareers: formatOccupationPreferences(occupationPreferences),
  };
};
